import { useState, useEffect, useRef, useCallback } from 'react'
import { Howl } from 'howler'
import { useStore } from '../store/useStore'

const MUSIC_PATH = '/music/ambient.mp3'

export default function BackgroundMusic() {
  const [playing, setPlaying] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const soundRef = useRef<Howl | null>(null)
  const language = useStore(state => state.language)

  useEffect(() => {
    const sound = new Howl({
      src: [MUSIC_PATH],
      html5: true,
      loop: true,
      volume: 0.3,
      onload: () => setLoaded(true),
      onloaderror: () => setError(true),
      onplayerror: () => setError(true),
    })
    soundRef.current = sound

    return () => {
      sound.unload()
    }
  }, [])

  useEffect(() => {
    if (!loaded || error) return
    const sound = soundRef.current
    if (!sound) return

    const tryPlay = () => {
      if (!sound.playing()) {
        sound.play()
        setPlaying(true)
      }
    }

    tryPlay()

    const handler = () => tryPlay()
    document.addEventListener('click', handler, { once: true })
    document.addEventListener('touchstart', handler, { once: true })
    document.addEventListener('keydown', handler, { once: true })

    return () => {
      document.removeEventListener('click', handler)
      document.removeEventListener('touchstart', handler)
      document.removeEventListener('keydown', handler)
    }
  }, [loaded, error])

  const toggle = useCallback(() => {
    const sound = soundRef.current
    if (!sound) return

    if (playing) {
      sound.pause()
      setPlaying(false)
    } else {
      sound.play()
      setPlaying(true)
    }
  }, [playing])

  if (error) return null

  const t = language === 'zh-CN'

  return (
    <button
      type="button"
      onClick={toggle}
      className="text-sepia-400 hover:text-white transition-colors p-1"
      aria-label={playing ? (t ? '关闭音乐' : 'Mute music') : (t ? '播放音乐' : 'Play music')}
      title={playing ? (t ? '关闭音乐' : 'Mute music') : (t ? '播放音乐' : 'Play music')}
    >
      {playing ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M9 10l6 4-6 4V10z" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
        </svg>
      )}
    </button>
  )
}
